import styled from '@emotion/styled';
import {
  cloneElement,
  type HTMLAttributes,
  isValidElement,
  type MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { tokens } from '@sunrun/experience-ui-components';

type InfoPopoverProps = {
  content: ReactNode;
  children: ReactElement<
    HTMLAttributes<HTMLElement> & {
      onClick?: (e: ReactMouseEvent<HTMLElement>) => void;
    }
  >;
  contentTestId?: string;
  contentAriaLabel?: string;
};

const ON_INFO_SURFACE = 'rgba(73, 81, 125, 1)';

const VIEW_MARGIN = 12;
const GAP_ABOVE = 10;
const GAP_BELOW = 10;

type PopoverLayoutAbove = {
  placement: 'above';
  top: number;
  left: number;
  caretLeft: number;
};

type PopoverLayoutBelow = {
  placement: 'below';
  top: number;
  left: number;
  caretLeft: number;
};

type PopoverLayout = PopoverLayoutAbove | PopoverLayoutBelow;

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function layoutsEqual(
  a: PopoverLayout | null,
  b: PopoverLayout | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.placement === b.placement &&
    a.top === b.top &&
    a.left === b.left &&
    a.caretLeft === b.caretLeft
  );
}

const InfoPopover = ({
  content,
  children,
  contentTestId,
  contentAriaLabel = 'Details',
}: InfoPopoverProps) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<PopoverLayout | null>(null);
  const contentId = useId();

  const close = useCallback(() => setOpen(false), []);

  const updatePosition = useCallback(() => {
    const anchorEl = wrapRef.current;
    const popEl = popoverRef.current;
    if (!anchorEl || !popEl) return;

    const anchor = anchorEl.getBoundingClientRect();
    const pop = popEl.getBoundingClientRect();
    const anchorCenterX = anchor.left + anchor.width / 2;

    const idealAboveTop = anchor.top - pop.height - GAP_ABOVE;
    const hasRoomAbove = idealAboveTop >= VIEW_MARGIN;

    let left = anchorCenterX - pop.width / 2;
    left = clamp(
      left,
      VIEW_MARGIN,
      window.innerWidth - pop.width - VIEW_MARGIN,
    );
    const caretLeft = anchorCenterX - left;

    let next: PopoverLayout;

    if (hasRoomAbove) {
      next = { placement: 'above', top: idealAboveTop, left, caretLeft };
    } else {
      let top = anchor.bottom + GAP_BELOW;
      top = clamp(
        top,
        VIEW_MARGIN,
        window.innerHeight - pop.height - VIEW_MARGIN,
      );
      next = { placement: 'below', top, left, caretLeft };
    }

    setLayout((prev) => (layoutsEqual(prev, next) ? prev : next));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setLayout(null);
      return;
    }
    updatePosition();
    const id = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(id);
  }, [open, content, updatePosition]);

  useLayoutEffect(() => {
    if (!open) return;
    const popEl = popoverRef.current;
    if (!popEl || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      updatePosition();
    });
    ro.observe(popEl);
    return () => ro.disconnect();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const trigger = isValidElement(children)
    ? cloneElement(children, {
        onClick: (e: ReactMouseEvent<HTMLElement>) => {
          children.props.onClick?.(e);
          setOpen((v) => !v);
        },
        'aria-expanded': open,
        'aria-haspopup': 'dialog' as const,
        'aria-controls': contentId,
        ...(open ? { 'aria-describedby': contentId } : {}),
      })
    : children;

  const portal =
    open &&
    typeof document !== 'undefined' &&
    createPortal(
      <PopoverSurface
        ref={popoverRef}
        data-testid={contentTestId}
        id={contentId}
        role="dialog"
        aria-label={contentAriaLabel}
        style={
          layout
            ? {
                position: 'fixed',
                top: layout.top,
                left: layout.left,
                zIndex: 6000,
                visibility: 'visible',
              }
            : {
                position: 'fixed',
                top: 0,
                left: 0,
                zIndex: 6000,
                visibility: 'hidden',
                pointerEvents: 'none',
              }
        }>
        <PopoverInner>{content}</PopoverInner>
        {layout && layout.placement === 'above' && (
          <CaretBelow
            style={{ left: layout.caretLeft, transform: 'translateX(-50%)' }}
          />
        )}
        {layout && layout.placement === 'below' && (
          <CaretAbove
            style={{
              left: layout.caretLeft,
              transform: 'translateX(-50%)',
              top: -9,
            }}
          />
        )}
      </PopoverSurface>,
      document.body,
    );

  return (
    <Anchor ref={wrapRef}>
      {trigger}
      {portal}
    </Anchor>
  );
};

const Anchor = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
`;

const PopoverSurface = styled.div`
  box-sizing: border-box;
  width: max-content;
  max-width: min(400px, calc(100vw - ${VIEW_MARGIN * 2}px));
  padding: 14px 10px 12px;
  border-radius: 12px;
  background-color: ${tokens.INFO_80};
  color: ${ON_INFO_SURFACE};
  box-shadow: 0 4px 18px rgba(31, 38, 71, 0.12);
  font-family: var(--font-families-roobert, 'Roobert', system-ui, sans-serif);
  font-size: 13px;
  line-height: 1.3;
  animation: infoPopoverIn 120ms ease-out;

  @keyframes infoPopoverIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const CaretBelow = styled.div`
  position: absolute;
  top: 100%;
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 9px solid ${tokens.INFO_80};
`;

const CaretAbove = styled.div`
  position: absolute;
  top: 0;
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 9px solid ${tokens.INFO_80};
`;

const PopoverInner = styled.div`
  position: relative;
  z-index: 1;
`;

export { InfoPopover, ON_INFO_SURFACE };
